import dayjsSchema from '@data/schemas/dayjsSchema'
import { dayJsMax } from '@data/schemas/util/dayjs'
import { Dayjs } from '@util/dayjs'
import passwordAtLeast6CharactersLengthLabel from '@util/labels/passwordAtLeast6CharactersLength'
import passwordsDontMatchLabel from '@util/labels/passwordsDontMatch'
import pleaseAcceptTermsLabel from '@util/labels/pleaseAcceptTerms'
import pleaseCompleteThisMandatoryFieldLabel from '@util/labels/pleaseCompleteThisMandatoryField'
import pleaseEnterAValidBirthdayLabel from '@util/labels/pleaseEnterAValidBirthday'
import pleaseEnterValidEmailLabel from '@util/labels/pleaseEnterValidEmail'
import startTypingAndSelectACity from '@util/labels/startTypingAndSelectACity'
import { birthdayMax } from '@util/member/birthdayMax'
import { ReactElement } from 'react'
import type { ObjectSchema } from 'yup'
import { boolean, object, ref, string } from 'yup'

type LocationPayloadData = {
    location?: {
        city?: TLocation['city'] | null
        country?: TLocation['country']['iocCode']
        coordinates?: TCoordinates
    }
}

export type TRegistrationSubmit = {
    firstName?: string
    lastName?: string
    email?: string
    password?: {
        password?: string
        repeat_password?: string
    }
    gender?: string
    birthday?: {
        day?: number
        month?: number
        year?: number
    }
    origin?: LocationPayloadData
    residency?: LocationPayloadData
    termsConditionsPrivacy?: boolean
    phoneNumber?: string
}

export type BasicRegistrationFormErrors = {
    firstName?: string
    lastName?: string
    password?: string
    repeatPassword?: string
    birthday?: string
    gender?: string
    bornIn?: string
    livingIn?: string
    terms?: string
    email?: string | ReactElement
    phoneNumber?: string
}

type EmailError = {
    email?: string
}

export const maxLengthName = 30

export type BasicRegistrationFormErrorResponse = BasicRegistrationFormErrors | EmailError

export type DowngradeMutationErrorResponse = {
    form: {
        errors?: Omit<BasicRegistrationFormErrorResponse, 'email'> & EmailError
    }
}

export type BasicFormStepType = 'FirstStep' | 'SecondStep'

type validationSchemaType = {
    [k in BasicFormStepType]: ObjectSchema<object>
}

const VALID_EMAIL_REGEXP =
    // eslint-disable-next-line @internations/curly-quotes
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const validationSchema: validationSchemaType = {
    FirstStep: object<BasicRegistrationFormErrors>().shape({
        firstName: string()
            .max(maxLengthName)
            .required(pleaseCompleteThisMandatoryFieldLabel)
            .default(() => ''),
        lastName: string()
            .max(maxLengthName)
            .required(pleaseCompleteThisMandatoryFieldLabel)
            .default(() => ''),
        email: string()
            .required(pleaseCompleteThisMandatoryFieldLabel)
            .matches(VALID_EMAIL_REGEXP, {
                message: pleaseEnterValidEmailLabel,
            })
            .default(() => ''),
        password: string()
            .required(pleaseCompleteThisMandatoryFieldLabel)
            .min(6, passwordAtLeast6CharactersLengthLabel)
            .default(() => ''),
        repeatPassword: string()
            .required(pleaseCompleteThisMandatoryFieldLabel)
            .min(6, passwordAtLeast6CharactersLengthLabel)
            .oneOf([ref('password')], passwordsDontMatchLabel)
            .default(() => ''),
    }),
    SecondStep: object<TRegistrationSubmit>().shape({
        gender: string().required(pleaseCompleteThisMandatoryFieldLabel).default(undefined),
        birthday: dayjsSchema()
            .required(pleaseEnterAValidBirthdayLabel)
            .typeError(pleaseEnterAValidBirthdayLabel)
            .test('latest allowed birthdate', pleaseEnterAValidBirthdayLabel, dayJsMax(birthdayMax)),
        bornIn: object()
            .defined()
            .required(startTypingAndSelectACity)
            .default(() => null),
        livingIn: object()
            .defined()
            .required(startTypingAndSelectACity)
            .default(() => null),
        terms: boolean().required(pleaseAcceptTermsLabel).isTrue(pleaseAcceptTermsLabel).default(undefined),
    }),
}

export interface BasicFormValues {
    firstName?: string
    lastName?: string
    email?: string
    password?: string
    repeatPassword?: string
    birthday?: Dayjs
    gender?: TGender
    bornIn?: TLocation
    livingIn?: TLocation
    terms?: boolean
    phoneNumber?: string
}

export function transformDataToSubmit(formData: BasicFormValues): TRegistrationSubmit {
    const month = formData?.birthday ? formData.birthday.month() + 1 : undefined
    const birthday = {
        day: formData?.birthday?.date(),
        month,
        year: formData?.birthday?.year(),
    }
    const termsConditionsPrivacy = formData.terms
    return {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: { password: formData.password, repeat_password: formData.repeatPassword },
        gender: formData.gender,
        birthday,
        origin: {
            location: {
                city: formData?.bornIn?.city,
                country: formData?.bornIn?.country?.iocCode,
                coordinates: formData?.bornIn?.coordinates,
            },
        },
        residency: {
            location: {
                city: formData?.livingIn?.city,
                country: formData?.livingIn?.country?.iocCode,
                coordinates: formData?.livingIn?.coordinates,
            },
        },
        termsConditionsPrivacy,
        phoneNumber: formData?.phoneNumber,
    }
}

export type ErrorRegistrationFormValues = {
    'password.password'?: string
}
